import React from 'react';
import { Box, Button } from '@mui/material';
import { ChevronRight as ChevronIcon, GroupAddOutlined as GroupAddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { ContextBalances } from '../../services/contextApi';
import { summarizeBalances } from '../../services/balances';
import { formatMoneyCents } from '../../theme/chart';
import { tokens } from '../../theme/tokens';
import { Panel, PanelHeader, Quiet, RowButton, Tile } from './home/ui';

interface Props {
  all: ContextBalances[];
  /** whether the user is in any group at all, settled or not */
  hasGroups: boolean;
  currentUserId?: string;
  onOpenGroup: (contextId: string) => void;
  onSeeGroups: () => void;
  onCreateGroup: () => void;
}

/**
 * "Entre grupos", the personal view's bridge to the groups: what the user
 * has to receive and to pay, and where. Without a group yet, it says what a
 * group is for and how to start one.
 */
export const ReceivablesCard: React.FC<Props> = ({ all, hasGroups, currentUserId, onOpenGroup, onSeeGroups, onCreateGroup }) => {
  const { t, i18n } = useTranslation('dashboard');
  const title = t('receivables.title');
  const money = (value: number, currency: string) => formatMoneyCents(value, currency, i18n.language);

  if (!hasGroups) {
    return (
      <Panel label={title}>
        <PanelHeader title={title} />
        <Box component='p' sx={{ m: 0, fontSize: 14, color: tokens.ink2, lineHeight: 1.5 }}>{t('receivables.noGroups')}</Box>
        <Button variant='outlined' startIcon={<GroupAddIcon />} onClick={onCreateGroup} sx={{ alignSelf: 'flex-start', minHeight: 44 }}>
          {t('receivables.createGroup')}
        </Button>
      </Panel>
    );
  }

  const summary = summarizeBalances(all, currentUserId ?? '');
  const totals = summary.totals.length ? summary.totals : [{ currency: all[0]?.currency ?? 'BRL', receivable: 0, payable: 0 }];

  const joinNames = (names: string[]) =>
    names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} ${t('group.and')} ${names[names.length - 1]}`;

  // Who is behind each group's number, in words.
  const describe = (contextId: string): string => {
    const balances = all.find((entry) => entry.contextId === contextId);
    if (!balances || !currentUserId) return '';
    const name = (userId: string) => balances.members.find((member) => member.userId === userId)?.firstName ?? '?';
    const debtors = balances.pairs.filter((pair) => pair.toUserId === currentUserId).map((pair) => name(pair.fromUserId));
    const creditors = balances.pairs.filter((pair) => pair.fromUserId === currentUserId).map((pair) => name(pair.toUserId));
    const parts: string[] = [];
    if (debtors.length) parts.push(t(debtors.length === 1 ? 'receivables.owesYouOne' : 'receivables.owesYouMany', { names: joinNames(debtors) }));
    if (creditors.length) parts.push(t('receivables.youOwe', { names: joinNames(creditors) }));
    return parts.join(' · ');
  };

  return (
    <Panel label={title} sx={{ gap: 1.75 }}>
      <PanelHeader title={title} action={{ label: t('receivables.seeGroups'), onClick: onSeeGroups }} />
      {totals.map((total) => (
        <Box key={total.currency} sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.25 }}>
          <Tile label={t('receivables.receivable')} value={money(total.receivable, total.currency)} tone={total.receivable > 0 ? 'accent' : 'plain'} size={22} />
          <Tile label={t('receivables.payable')} value={money(total.payable, total.currency)} valueColor={total.payable > 0 ? tokens.warn : undefined} size={22} />
        </Box>
      ))}
      {summary.contexts.length === 0 ? (
        <Quiet>{t('receivables.allSettled')}</Quiet>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {summary.contexts.map((context) => {
            const net = Math.round((context.receivable - context.payable) * 100) / 100;
            return (
              <RowButton key={context.contextId} onClick={() => onOpenGroup(context.contextId)}>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <Box component='span' sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{context.contextName}</Box>
                  <Box component='span' sx={{ fontSize: 12.5, color: tokens.ink3 }}>{describe(context.contextId)}</Box>
                </Box>
                <Box
                  component='span'
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 700, whiteSpace: 'nowrap', color: net >= 0 ? tokens.accent : tokens.warn }}
                >
                  {net >= 0 ? '+' : '−'}
                  {money(Math.abs(net), context.currency)}
                  <ChevronIcon aria-hidden sx={{ fontSize: 18 }} />
                </Box>
              </RowButton>
            );
          })}
        </Box>
      )}
    </Panel>
  );
};
